import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { formatDate, formatDateTime, formatRelative, toISO } from "./dateFormat";

// Re-export the app-wide date/time formatters so any file that already
// imports from "lib/utils" can just call formatDate / formatDateTime.
// Single source of truth lives in lib/dateFormat.js.
export { formatDate, formatDateTime, formatRelative as formatRelativeDate, toISO };

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * Coerce a fetch/axios error into a display-safe string.
 *
 * FastAPI validation errors return `detail` as a *list* of objects
 * (e.g. `[{type, loc, msg, input, url}]`). Rendering that array as a
 * React child throws "Objects are not valid as a React child" and, if
 * the throw happens inside a state update, blows up the whole route
 * via the error boundary. This helper normalizes everything: strings
 * pass through, arrays are mapped to their `.msg`, single objects fall
 * back to `.detail`/`.message`/`.msg`, and unknown shapes yield a
 * generic fallback the caller can override.
 *
 * Never returns an object. Never throws. Safe to pass straight into
 * `toast.error(...)` or a `<span>{msg}</span>`.
 */
export function toErrorMessage(err, fallback = "Something went wrong") {
  if (!err) return fallback;
  // Axios shape → err.response.data
  const data = err?.response?.data ?? err?.data ?? err;
  const detail = data?.detail ?? data?.message ?? data;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === "string" ? d : d?.msg || d?.message || ""))
      .filter(Boolean);
    if (msgs.length) return msgs.join("; ");
  }
  if (detail && typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
  }
  if (typeof err?.message === "string") return err.message;
  return fallback;
}

// Authenticated file URL (SEC-002). Server-side /api/uploads now requires a
// JWT; browsers can't send auth headers on <img>/<a>, so we append the token
// as a query param. External (http) URLs and blob/data URLs pass through
// unchanged. Use this anywhere a file path is placed directly in src/href.
export function fileUrl(url) {
  if (!url) return url;
  if (/^(https?:|blob:|data:)/i.test(url)) return url;
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const abs = url.startsWith("/api/") ? url : `/api${url.startsWith("/") ? "" : "/"}${url}`;
  // Auth rides on the httpOnly session cookie now (DEF-23) — no JWT in the URL.
  return `${base}${abs}`;
}

// Secure voicemail audio URL. Streams through the authenticated backend proxy
// (never a raw Twilio media URL — PHI). Appends the user's JWT as a query param
// so <audio src> works without auth headers.
export function voicemailAudioUrl(id) {
  if (!id) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  // In-app <audio> playback authenticates via the httpOnly session cookie (DEF-23).
  return `${base}/api/twilio/voicemails/${id}/audio`;
}

export function formatPhoneNumber(phone) {
  if (!phone) return "";
  // Never leak internal identifiers like `client:<uuid>` into display.
  // Callers should ideally filter these out, but keep a belt-and-braces
  // guard here so any stray leaked value renders as "Internal caller"
  // instead of a raw UUID (bug #2 in the /phone rebuild spec).
  if (typeof phone === "string" && phone.startsWith("client:")) return "Internal caller";
  const cleaned = phone.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11) {
    return `+${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

export function formatDuration(seconds) {
  if (!seconds || seconds === 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}

export function formatRelativeTime(date) {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  return formatDate(then);
}

export function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();
}

export function getStatusColor(status) {
  switch (status) {
    case "available":
      return "bg-emerald-500";
    case "busy":
      return "bg-red-500";
    case "away":
      return "bg-amber-500";
    case "offline":
    default:
      return "bg-slate-500";
  }
}

export function getCallTypeIcon(type) {
  switch (type) {
    case "incoming":
      return "phone-incoming";
    case "outgoing":
      return "phone-outgoing";
    case "missed":
      return "phone-missed";
    default:
      return "phone";
  }
}

export function getCallTypeColor(type) {
  switch (type) {
    case "incoming":
      return "text-emerald-500";
    case "outgoing":
      return "text-blue-500";
    case "missed":
      return "text-red-500";
    default:
      return "text-slate-500";
  }
}


export function getCompanyBorderColor(name) {
  if (!name) return "#3b82f6";
  const n = name.toLowerCase();
  if (n.includes("institute")) return "#ef4444";
  if (n.includes("avida")) return "#eab308";
  return "#3b82f6";
}

export function getCompanyTheme(name) {
  if (!name) return { border: "#3b82f6", bg: "bg-blue-50", text: "text-blue-700", icon: "text-blue-500" };
  const n = name.toLowerCase();
  if (n.includes("institute")) return { border: "#ef4444", bg: "bg-red-50", text: "text-red-700", icon: "text-red-500" };
  if (n.includes("avida")) return { border: "#eab308", bg: "bg-yellow-50", text: "text-yellow-700", icon: "text-yellow-500" };
  return { border: "#3b82f6", bg: "bg-blue-50", text: "text-blue-700", icon: "text-blue-500" };
}
